import { coins } from '../format';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/features/auth/use-auth';
import { useAuthDialog } from '@/features/auth/use-auth-dialog';
import { useMarketCheckout, useMarketProducts, type MarketOrder, type MarketProduct } from '../api';
import {
  checkoutError,
  isUncertainCheckout,
  readPendingCheckout,
  savePendingCheckout,
  type PendingCheckout,
} from '../checkout';
import { CheckoutDialog } from '../components/CheckoutDialog';
import { AccountStrip, CoinMark, LoadError, MarketHero } from '../components/MarketParts';
import { MyOrders } from '../components/MyOrders';

export function MarketPage() {
  const { user } = useAuth();
  return <MarketSession key={user?.id ?? 'guest'} userId={user?.id ?? null} />;
}

function MarketSession({ userId }: { userId: string | null }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { open } = useAuthDialog();
  const products = useMarketProducts();
  const checkout = useMarketCheckout();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState<MarketProduct | null>(null);
  const [pending, setPending] = useState<PendingCheckout | null>(() =>
    userId ? readPendingCheckout(userId) : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<MarketOrder | null>(null);
  const submitting = useRef(false);
  const categories = [...new Set(products.data?.map((product) => product.category) ?? [])];
  const visible =
    products.data?.filter(
      (product) =>
        (!category || product.category === category) &&
        `${product.title} ${product.description}`
          .toLocaleLowerCase('tr-TR')
          .includes(search.toLocaleLowerCase('tr-TR').trim()),
    ) ?? [];
  const currentProduct =
    pending?.product ?? products.data?.find((product) => product.id === selected?.id) ?? selected;

  function choose(product: MarketProduct) {
    if (isLoading) return;
    if (!isAuthenticated) {
      open('login');
      return;
    }
    setReceipt(null);
    setError(null);
    setSelected(pending?.product ?? product);
  }

  async function submit(deliveryDetails: string | null) {
    if (!userId || !currentProduct || submitting.current) return;
    const intent = pending ?? {
      product: currentProduct,
      request: {
        productId: currentProduct.id,
        requestId: crypto.randomUUID(),
        expectedCoinPrice: currentProduct.coinPrice,
        deliveryDetails,
      },
    };
    try {
      savePendingCheckout(userId, intent);
    } catch {
      setError(
        'Siparişini güvenle saklayamadık. Tarayıcı oturum depolamasını etkinleştirip tekrar dene.',
      );
      return;
    }
    submitting.current = true;
    setPending(intent);
    setError(null);
    try {
      const order = await checkout.mutateAsync(intent.request);
      try {
        savePendingCheckout(userId, null);
        setPending(null);
      } catch {
        // The server confirmed this order. Preserve its key if browser cleanup
        // fails; repeating it remains safe, including after a reload.
        setError(
          'Siparişin alındı; tarayıcı kaydı temizlenemedi. Aynı işlemi kontrol ederek devam edebilirsin.',
        );
      }
      setSelected(null);
      setReceipt(order);
    } catch (reason) {
      setError(checkoutError(reason));
      if (!isUncertainCheckout(reason)) {
        try {
          savePendingCheckout(userId, null);
          setPending(null);
        } catch {
          /* Keep the exact intent until local storage is available. */
        }
      }
    } finally {
      submitting.current = false;
    }
  }

  return (
    <div className="market-page">
      <MarketHero />
      <AccountStrip />
      {pending ? (
        <div className="market-pending" role="status">
          <div>
            <strong>Bir siparişin sonucu bekleniyor</strong>
            <p>{pending.product.title} · Yeni bir seçimden önce bu işlemi kontrol et.</p>
          </div>
          <Button
            variant="secondary"
            loading={checkout.isPending}
            onClick={() => setSelected(pending.product)}
          >
            Siparişi kontrol et
          </Button>
        </div>
      ) : null}
      {receipt ? (
        <div className="market-receipt" role="status">
          <span aria-hidden>✓</span>
          <div>
            <strong>
              {receipt.status === 'cancelled'
                ? 'Siparişin iptal edilmiş.'
                : receipt.status === 'fulfilled'
                  ? 'Siparişin tamamlandı.'
                  : 'Siparişin alındı.'}
            </strong>
            <p>
              {receipt.productTitle} · {coins(receipt.coinPrice)} GC
              {receipt.status === 'cancelled' ? ' iade edildi' : ''}
            </p>
          </div>
          <a href="#market-orders" className="market-text-link">
            Siparişimi takip et ↗
          </a>
        </div>
      ) : null}
      <section id="market-catalog" aria-labelledby="market-catalog-title">
        <div className="market-section-heading">
          <div>
            <p className="market-eyebrow">SANA AİT BİR SEÇİM</p>
            <h2 id="market-catalog-title">Market</h2>
          </div>
          <span className="market-outline-label">GeeCoin ile keşfet</span>
        </div>
        {products.isPending ? (
          <div className="market-product-grid" role="status" aria-label="Ürünler yükleniyor">
            {[0, 1, 2].map((i) => (
              <div key={i} className="market-skeleton" />
            ))}
          </div>
        ) : products.isError ? (
          <LoadError
            label="Market ürünleri yüklenemedi."
            retry={() => {
              void products.refetch();
            }}
          />
        ) : products.data.length === 0 ? (
          <EmptyState
            title="Market’in ilk koleksiyonu hazırlanıyor"
            description="Ürünler yayınlandığında burada yerini alacak. Bu sırada GeeCoin hesabını keşfedebilirsin."
          />
        ) : (
          <>
            <div className="market-filters">
              <div className="market-categories" role="group" aria-label="Ürün kategorisi">
                <button aria-pressed={!category} onClick={() => setCategory('')}>
                  Tümü <span>{products.data.length}</span>
                </button>
                {categories.map((item) => (
                  <button
                    key={item}
                    aria-pressed={category === item}
                    onClick={() => setCategory(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <label className="market-search">
                <svg
                  aria-hidden
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                >
                  <circle cx="10.5" cy="10.5" r="6.5" />
                  <path d="m16 16 4 4" />
                </svg>
                <input
                  aria-label="Ürün ara"
                  type="search"
                  placeholder="Koleksiyonda ara…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
            </div>
            <p className="market-results" aria-live="polite">
              {visible.length} ürün
            </p>
            {visible.length === 0 ? (
              <EmptyState
                title="Bu aramada bir ürün bulamadık"
                description="Başka bir kelime veya kategori deneyebilirsin."
              >
                <Button
                  variant="secondary"
                  className="mt-5"
                  onClick={() => {
                    setSearch('');
                    setCategory('');
                  }}
                >
                  Filtreleri temizle
                </Button>
              </EmptyState>
            ) : (
              <div className="market-product-grid">
                {visible.map((product) => (
                  <article key={product.id} className="market-product" data-tilt>
                    <div className="market-product-image">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.title}
                          loading="lazy"
                          width={600}
                          height={480}
                        />
                      ) : (
                        <div className="market-product-no-image">
                          <CoinMark />
                          <span>GEEFAMILY COLLECTION</span>
                        </div>
                      )}
                      <span
                        className={`market-stock${product.stock === 0 ? ' market-sold-out' : ''}`}
                      >
                        {product.stock === 0
                          ? 'Tükendi'
                          : product.stock === null
                            ? 'Stokta'
                            : `${product.stock} adet kaldı`}
                      </span>
                    </div>
                    <div className="market-product-content">
                      <p className="market-eyebrow">{product.category}</p>
                      <h3>{product.title}</h3>
                      <p className="market-product-description">{product.description}</p>
                      <div className="market-product-bottom">
                        <p className="market-product-price">
                          <CoinMark small />
                          <strong>{coins(product.coinPrice)}</strong>
                          <span>GC</span>
                        </p>
                        <Button
                          variant="secondary"
                          disabled={
                            product.stock === 0 ||
                            isLoading ||
                            (Boolean(pending) && pending?.request.productId !== product.id)
                          }
                          onClick={() => choose(product)}
                          aria-label={`${product.title} ürününü incele`}
                        >
                          {product.stock === 0 ? 'Tükendi' : 'İncele'} <span aria-hidden>↗</span>
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </section>
      {isAuthenticated ? (
        <MyOrders />
      ) : (
        <section className="market-join">
          <div>
            <p className="market-eyebrow">GEEFAMILY’DE YERİN HAZIR</p>
            <h2>Birlikte kazan, kendin seç.</h2>
            <p>Etkinliklerden gelen puanlarını ve tüm Market siparişlerini hesabında takip et.</p>
          </div>
          <Button disabled={isLoading} onClick={() => open('login')}>
            Hesabına giriş yap ↗
          </Button>
        </section>
      )}
      {selected && currentProduct && isAuthenticated ? (
        <CheckoutDialog
          key={currentProduct.id}
          product={currentProduct}
          pending={pending}
          busy={checkout.isPending}
          error={error}
          onClose={() => setSelected(null)}
          onSubmit={(details) => {
            void submit(details);
          }}
        />
      ) : null}
    </div>
  );
}
